/* Explicit product-reference or tag matches. Consent never defaults to yes. */
(function (root) {
  'use strict';
  const ATTRIBUTE = 'matraws_akryl_v1';
  const norm = value => String(value ?? '').trim().toLowerCase();
  const isCase = item => (Array.isArray(item.fits) && item.fits.length > 0) || norm(item.fitsTag) !== '';
  // Single source of truth for "B fits in case C", shared by validation and UI.
  function fitsIn(box, casing) {
    if (!box || !casing || String(box.id) === String(casing.id)) return false;
    if (Array.isArray(casing.fits) && casing.fits.map(String).includes(String(box.productId))) return true;
    const tag = norm(casing.fitsTag);
    if (!tag || isCase(box) || String(box.productId) === String(casing.productId)) return false;
    return Array.isArray(box.tags) && box.tags.some(t => norm(t) === tag);
  }
  function normalise(items, requested) {
    const byId = new Map();
    for (const item of items) {
      const id = String(item.id);
      const previous = byId.get(id);
      byId.set(id, { ...item, id, quantity: (previous?.quantity || 0) + item.quantity });
    }
    const left = new Map([...byId].map(([id, item]) => [id, item.quantity]));
    const result = [], seen = new Set();
    for (const pair of Array.isArray(requested) ? requested.slice(0, 50) : []) {
      if (!pair || typeof pair !== 'object') continue;
      const box = byId.get(String(pair.b)), casing = byId.get(String(pair.c));
      const key = pair.b + ':' + pair.c;
      if (!box || !casing || box.id === casing.id || seen.has(key) ||
          !fitsIn(box, casing) || !Number.isInteger(pair.n) || pair.n < 1) continue;
      seen.add(key);
      const n = Math.min(pair.n, left.get(box.id), left.get(casing.id));
      if (n > 0) {
        result.push({ b: box.id, c: casing.id, n });
        left.set(box.id, left.get(box.id) - n);
        left.set(casing.id, left.get(casing.id) - n);
      }
    }
    return result;
  }
  function parse(value) { try { const p = JSON.parse(value || '[]'); return Array.isArray(p) ? p : []; } catch { return []; } }
  // Export the actual production allocation logic for regression tests.
  if (typeof module !== 'undefined' && module.exports) { module.exports = { normalise, parse, fitsIn }; return; }
  if (root.MtwAcrylic) return;
  root.MtwAcrylic = true;
  const url = (root.Shopify?.routes?.root || '/') + 'cart';
  const metadata = new Map();
  let items = [], selected = [], busy = false, error = '', rerun = false;
  let allowSubmit = false, submitting = false;
  const mounts = () => [...document.querySelectorAll('[data-mtw-acrylic]')];
  const esc = value => String(value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const decode = value => { const el=document.createElement('textarea'); el.innerHTML=String(value).replace(/</g,'&lt;').replace(/>/g,'&gt;'); return el.value; };
  async function request(path, data) {
    const response = await fetch(url + path, data ? {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data)} : {cache:'no-store'});
    if (!response.ok) throw new Error('Kunne ikke gemme pakkevalget. Prøv igen før du går til betaling.');
    return response.json();
  }
  function candidates() {
    const unique = [...new Map(items.map(item => [item.id, item])).values()];
    return unique.flatMap(c => unique.filter(b => fitsIn(b, c)).map(b => ({b,c})));
  }
  function render() {
    const pairs = candidates();
    for (const mount of mounts()) {
      mount.hidden = !pairs.length && !error;
      const content = mount.querySelector('[data-acrylic-content]');
      content.innerHTML = '<h3>Skal vi pakke det for dig?</h3><details class="mtw-acrylic-explanation"><summary>Sådan pakker vi</summary><p>Vi lægger produktet i den akrylboks eller holder, du køber med. Den originale forsegling forbliver ubrudt.</p></details>' + pairs.map(({b,c}) => {
        const pick = selected.find(p => p.b === b.id && p.c === c.id);
        const possible = normalise(items, [...selected.filter(p => p !== pick), {b:b.id,c:c.id,n:999999}]).find(p => p.b === b.id && p.c === c.id)?.n || 0;
        return '<div class="mtw-acrylic-pair"><label><input type="checkbox" data-acrylic-box="'+b.id+'" data-acrylic-case="'+c.id+'" '+(pick?'checked ':'')+((busy || !possible)?'disabled ':'')+'><span><strong>Ja tak – pak dem sammen</strong><small>'+esc(b.title)+'<br>i '+esc(c.title)+'</small></span></label>' + (pick ? '<label class="mtw-acrylic-quantity">Antal <input aria-label="Antal '+esc(b.title)+' der skal pakkes" type="number" min="1" max="'+possible+'" value="'+pick.n+'" data-acrylic-box="'+b.id+'" data-acrylic-case="'+c.id+'" '+(busy?'disabled':'')+'></label>' : '') + (!possible && !pick?'<small>De tilgængelige varer er allerede valgt ovenfor.</small>':'')+'</div>';
      }).join('') + '<p data-acrylic-status role="status" data-error="'+Boolean(error)+'">'+esc(error || (busy?'Gemmer…':selected.length?'Dit pakkevalg er gemt.':'Valgfrit – uden tilvalg pakker vi produkterne hver for sig.'))+'</p>' + (error?'<button type="button" data-acrylic-retry>Prøv igen</button>':'');
    }
    // Accelerated checkout must not outrun an in-flight consent update.
    document.querySelectorAll('.cart-dynamic-checkout-buttons').forEach(el => { el.inert = busy || Boolean(error); });
  }
  async function reconcile(desired) {
    if (busy) { rerun = true; return false; }
    busy = true; error = ''; render();
    try {
      const cart = await request('.js');
      items = cart.items.map(i => ({id:String(i.variant_id),productId:String(i.product_id),title:decode(i.title),quantity:i.quantity,...(metadata.get(String(i.product_id)) || {fits:[],tags:[],fitsTag:''})}));
      const prior = parse(cart.attributes?.[ATTRIBUTE]);
      selected = normalise(items, desired === undefined ? prior : desired);
      const value = selected.length ? JSON.stringify(selected) : '';
      if ((cart.attributes?.[ATTRIBUTE] || '') !== value) {
        const summary = selected.map(p => `${p.n} × ${items.find(i=>i.id===p.b).title} i ${items.find(i=>i.id===p.c).title}`).join('; ');
        await request('/update.js', {attributes:{[ATTRIBUTE]:value,'Pakning': summary ? 'Ja tak: '+summary+'. Original forsegling bevares.' : '','Pakning i akryl':''}});
      }
      return true;
    } catch (e) { error = e.message; return false; }
    finally { busy = false; render(); if (rerun) { rerun=false; void reconcile(); } }
  }
  function scan() {
    let found = false;
    for (const mount of mounts()) {
      if (mount.dataset.acrylicReady) continue;
      mount.dataset.acrylicReady = 'true';
      try { const data = JSON.parse(mount.querySelector('[data-acrylic-cart]').textContent); for(const i of data.items) metadata.set(String(i.productId),{fits:(i.fits || []).map(String),tags:(i.tags || []).map(norm),fitsTag:norm(i.fitsTag)}); found=true; } catch { /* No consent UI without valid server metadata. */ }
    }
    if (found) void reconcile();
    return found;
  }
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-acrylic-box]');
    if (!input || busy) return;
    const b=input.dataset.acrylicBox,c=input.dataset.acrylicCase;
    const next=selected.filter(p=>p.b!==b||p.c!==c);
    if(input.type==='number' || input.checked) next.push({b,c,n:input.type==='number'?Math.max(1,Math.floor(Number(input.value)||1)):1});
    void reconcile(next);
  });
  document.addEventListener('click', event => { if(event.target.closest('[data-acrylic-retry]')) void reconcile(selected); });
  document.addEventListener('submit', async event => {
    if (allowSubmit || event.submitter?.name !== 'checkout' || !mounts().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (submitting) return;
    submitting = true;
    // Recheck quantities even after a change in another tab; never trust stale DOM.
    try {
      while (busy) await new Promise(resolve => setTimeout(resolve, 50));
      if (error) return;
      if (await reconcile()) {
        while (busy) await new Promise(resolve => setTimeout(resolve, 50));
        if (error) return;
        allowSubmit=true;
        try { event.target.requestSubmit(event.submitter); } finally { allowSubmit=false; }
      }
    } finally { submitting=false; }
  }, true);
  document.addEventListener('mtw:cart-updated', () => { if (!scan()) void reconcile(); });
  // Kører ved hver DOM-ændring på siden, så der søges kun i de ændrede noder og ikke i hele dokumentet.
  const hasMount = nodes => [...nodes].some(n => n.nodeType === 1 && (n.matches('[data-mtw-acrylic]') || n.querySelector('[data-mtw-acrylic]')));
  new MutationObserver(records => {
    const removed = records.some(r => hasMount(r.removedNodes));
    const added = records.some(r => hasMount(r.addedNodes));
    if (!(added && scan()) && removed) void reconcile();
  }).observe(document.documentElement,{childList:true,subtree:true});
  scan();
})(typeof window !== 'undefined' ? window : globalThis);
